import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, Modal, Form, message, Popconfirm, Tag, Input, Card, Row, Col, Pagination, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, LoginOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons';
import { businessDomainApi } from '../../services/api';
import { formatDateTime } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
// 引入阿里员工组件
import EmployeeField from '@ali/deep-employee-field';
// 引入自定义的员工选择组件
import EmployeeSelector from '../../components/EmployeeSelector';

function BusinessDomainList() {
  const navigate = useNavigate();
  const { enterDomain } = useWorkspace();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    domainName: '',
    spaceType: undefined,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  // 添加管理员和成员的状态
  const [adminUsers, setAdminUsers] = useState([]);
  const [memberUsers, setMemberUsers] = useState([]);
  // 添加 ref 用于获取表单字段值
  const adminUsersRef = useRef(null);
  const memberUsersRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize, searchParams]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        domainName: searchParams.domainName || undefined,
        spaceType: searchParams.spaceType || undefined,
      };

      const response = await businessDomainApi.getList(params);

      // 根据实际API返回结构调整数据解析
      const dataList = response.records || response.list || [];
      const totalCount = response.total || 0;

      setDataSource(dataList);
      setPagination(prev => ({
        ...prev,
        total: totalCount,
      }));
    } catch (error) {
      console.error('获取项目空间列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchParams(prev => ({ ...prev, domainName: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSpaceTypeChange = (value) => {
    setSearchParams(prev => ({ ...prev, spaceType: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };



  const handleAdd = () => {
    setEditingRecord(null);
    setModalVisible(true);
    form.resetFields();
    // 重置管理员和成员状态
    setAdminUsers([]);
    setMemberUsers([]);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setModalVisible(true);

    // 处理管理员和成员数据格式，适配阿里员工组件
    const adminUsers = record.adminUserInfoList ?
      record.adminUserInfoList.map(user => ({
        workNo: user.workNo,
        displayName: user.nickName || user.name || '',
        label: user.nickName || user.name || '',
        value: user.workNo,
        name: user.name || '',
        nickName: user.nickName || ''
      })) : [];

    const memberUsers = record.memberUserInfoList ?
      record.memberUserInfoList.map(user => ({
        workNo: user.workNo,
        displayName: user.nickName || user.name || '',
        label: user.nickName || user.name || '',
        value: user.workNo,
        name: user.name || '',
        nickName: user.nickName || ''
      })) : [];

    // 设置状态
    setAdminUsers(adminUsers);
    setMemberUsers(memberUsers);

    // 设置表单字段值
    form.setFieldsValue({
      domainName: record.domainName,
      businessType: record.businessType || 'n',
      adminUsers: adminUsers,
      memberUsers: memberUsers,
    });
  };

  const handleDelete = async (id) => {
    try {
      await businessDomainApi.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      console.error('删除项目空间失败:', error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // 从状态中获取员工数据，如果ref中有值则优先使用ref中的值
      const adminUsersValue = adminUsersRef.current || adminUsers;
      const memberUsersValue = memberUsersRef.current || memberUsers;
      console.log('adminUsersValue:', adminUsersValue);
      console.log('memberUsersValue:', memberUsersValue);
      // 处理阿里员工组件的数据格式，提取工号
      const formattedValues = {
        ...values,
        adminUsers: adminUsersValue ? adminUsersValue.map(user => user.workNo).join(',') : '',
        memberUsers: memberUsersValue ? memberUsersValue.map(user => user.workNo).join(',') : ''
      };

      if (editingRecord) {
        await businessDomainApi.update(editingRecord.id, formattedValues);
        message.success('修改成功');
      } else {
        await businessDomainApi.create(formattedValues);
        message.success('新增成功');
      }

      setModalVisible(false);
      fetchData();
    } catch (error) {
         // 从状态中获取员工数据，如果ref中有值则优先使用ref中的值
              const adminUsersValue = adminUsersRef.current || adminUsers;
              const memberUsersValue = memberUsersRef.current || memberUsers;
              console.log('adminUsersValue---->:', adminUsersValue);
              console.log('memberUsersValue----->:', memberUsersValue);
      console.error('保存项目空间失败:', error);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination(paginationConfig);
  };

  // 进入项目空间
  const handleEnterDomain = (domain) => {
    enterDomain(domain);
    navigate('/business-scenario');
  };

  // 处理分页变化
  const handlePaginationChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '项目空间名称',
      dataIndex: 'domainName',
      key: 'domainName',
      width: 150,
    },
    {
      title: '权限类型',
      dataIndex: 'permissionType',
      key: 'permissionType',
      width: 120,
      render: (permissionType) => {
        switch (permissionType) {
          case 'ADMIN':
            return <Tag color="blue">管理员</Tag>;
          case 'MEMBER':
            return <Tag color="green">成员</Tag>;
          case 'NONE':
            return <Tag color="default">无权限</Tag>;
          default:
            return <Tag color="default">未知</Tag>;
        }
      },
    },
    {
      title: '管理员',
      dataIndex: 'adminUserInfoList',
      key: 'adminUsers',
      width: 200,
      render: (adminUserInfoList) => {
        const users = adminUserInfoList || [];
        return (
          <div>
            {/* 前三个管理员 */}
            <div>
              {users.slice(0, 3).map((user) => {
                const displayName = user.nickName || user.name || '';
                const link = `https://work.alibaba-inc.com/nwpipe/u/${user.workNo}`;
                return (
                  <a
                    key={user.workNo}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700 mr-2"
                  >
                    {displayName}
                  </a>
                );
              })}
            </div>
            {/* 超过三个的管理员换行展示 */}
            {users.length > 3 && (
              <div className="mt-1">
                {users.slice(3).map((user) => {
                  const displayName = user.nickName || user.name || '';
                  const link = `https://work.alibaba-inc.com/nwpipe/u/${user.workNo}`;
                  return (
                    <a
                      key={user.workNo}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 mr-2"
                    >
                      {displayName}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '成员',
      dataIndex: 'memberUserInfoList',
      key: 'memberUsers',
      width: 250,
      render: (memberUserInfoList) => (
        <div>
          {(memberUserInfoList || []).map((user) => {
            const displayName = user.nickName || user.name || '';
            const link = `https://work.alibaba-inc.com/nwpipe/u/${user.workNo}`;
            return (
              <a
                key={user.workNo}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-500 hover:text-green-700 mr-2"
              >
                {displayName}
              </a>
            );
          })}
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (time) => formatDateTime(time),
    },
   {
     title: '创建人',
     dataIndex: 'creatorInfo',
     key: 'creator',
     width: 200,
     render: (creatorInfo) => {
       if (!creatorInfo) return '-';

       const { name, workNo, nickName } = creatorInfo;
       const displayName = name || nickName || '';
       const link = workNo ? `https://work.alibaba-inc.com/nwpipe/u/${workNo}` : '';

       if (link) {
         return (
           <a
             href={link}
             target="_blank"
             rel="noopener noreferrer"
             className="text-blue-500 hover:text-blue-700"
           >
             {displayName}{workNo ? `(${workNo})` : ''}
           </a>
         );
       }

       return `${displayName}${workNo ? `(${workNo})` : ''}`;
     },
   },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => {
        // 根据权限类型动态显示操作按钮
        if (record.permissionType === 'ADMIN') {
          // 管理员：显示编辑和删除按钮
          return (
            <Space size="small">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                className="table-action-btn-edit"
              >
                编辑
              </Button>
              <Popconfirm
                title="确定要删除这个项目空间吗？"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  className="table-action-btn-delete"
                >
                  删除
                </Button>
              </Popconfirm>
            </Space>
          );
        } else if (record.permissionType === 'MEMBER') {
          // 成员：只显示查看权限，编辑和删除按钮禁用
          return (
            <Space size="small">
              <Button
                size="small"
                disabled
                title="成员权限：仅可查看"
                icon={<EditOutlined />}
                className="table-action-btn-edit"
                style={{ opacity: 0.5 }}
              >
                编辑
              </Button>
              <Button
                size="small"
                disabled
                title="成员权限：仅可查看"
                icon={<DeleteOutlined />}
                className="table-action-btn-delete"
                style={{ opacity: 0.5 }}
              >
                删除
              </Button>
            </Space>
          );
        } else if (record.permissionType === 'NONE') {
          // 无权限：不显示操作按钮
          return null;
        }
        // 默认情况：不显示操作按钮
        return null;
      },
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">项目空间管理</h1>
        <p className="page-description">管理系统中的项目空间，包括项目空间的创建、编辑和删除</p>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <Space size="middle">
          <Input.Search
            placeholder="请输入项目空间名称"
            allowClear
            suffix={<SearchOutlined />}
            size="large"
            className="w-80"
            onSearch={handleSearch}
          />
          <Select
            value={searchParams.spaceType}
            onChange={handleSpaceTypeChange}
            size="large"
            style={{ width: 140 }}
            allowClear
            placeholder="空间类型"
            options={[
              { label: '全部空间', value: 'all' },
              { label: '生产空间', value: 'real' },
              { label: '测试空间', value: 'test' },
            ]}
          />
        </Space>
        <Space size="middle">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleAdd}
            style={{
              background: '#1890FF',
              borderColor: '#1890FF',
              boxShadow: 'none'
            }}
          >
            新增项目空间
          </Button>
        </Space>
      </div>

      {/* 卡片展示 */}
      <div style={{ minHeight: '400px' }}>
        <Row gutter={[16, 16]}>
          {loading ? (
            <Col span={24}>
              <div style={{ textAlign: 'center', padding: '50px' }}>
                加载中...
              </div>
            </Col>
          ) : dataSource.length === 0 ? (
            <Col span={24}>
              <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                暂无数据
              </div>
            </Col>
          ) : (
            dataSource.map(domain => (
              <Col xs={24} sm={12} lg={8} xl={6} key={domain.id} style={{ minWidth: '280px' }}>
                <Card
                  hoverable
                  style={{
                    height: '220px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#fff',
                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)'
                  }}
                  styles={{
                    body: {
                      flex: 1,
                      overflow: 'hidden',
                      padding: '16px'
                    },
                    actions: {
                      marginTop: 'auto'
                    }
                  }}
                  actions={
                    domain.permissionType === 'ADMIN' ? [
                      <Button
                        key="edit"
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(domain)}
                        style={{ padding: '0 4px', fontSize: '12px' }}
                      >
                        编辑
                      </Button>,
                      <Popconfirm
                        key="delete"
                        title="确定要删除这个项目空间吗？"
                        onConfirm={() => handleDelete(domain.id)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button
                          type="link"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ padding: '0 4px', fontSize: '12px' }}
                        >
                          删除
                        </Button>
                      </Popconfirm>,
                      <Button
                        key="enter"
                        type="link"
                        size="small"
                        icon={<LoginOutlined />}
                        onClick={() => handleEnterDomain(domain)}
                        style={{ padding: '0 4px', fontSize: '12px' }}
                      >
                        进入
                      </Button>,
                    ] : domain.permissionType === 'MEMBER' ? [
                      <Button
                        key="edit"
                        type="link"
                        size="small"
                        disabled
                        icon={<EditOutlined />}
                        title="成员权限：仅可查看"
                        style={{ padding: '0 4px', fontSize: '12px' }}
                      >
                        编辑
                      </Button>,
                      <Button
                        key="delete"
                        type="link"
                        size="small"
                        disabled
                        danger
                        icon={<DeleteOutlined />}
                        title="成员权限：仅可查看"
                        style={{ padding: '0 4px', fontSize: '12px' }}
                      >
                        删除
                      </Button>,
                      <Button
                        key="enter"
                        type="link"
                        size="small"
                        icon={<LoginOutlined />}
                        onClick={() => handleEnterDomain(domain)}
                        style={{ padding: '0 4px', fontSize: '12px' }}
                      >
                        进入
                      </Button>,
                    ] : []
                  }
                >
                  {/* 右上角类型标识 */}
                  <span style={{
                    position: 'absolute',
                    top: '8px',
                    right: '12px',
                    padding: '2px 10px',
                    fontSize: '11px',
                    fontWeight: 500,
                    borderRadius: '10px',
                    color: domain.businessType === 'y' ? '#d46b08' : '#389e0d',
                    background: domain.businessType === 'y' ? '#fff7e6' : '#f6ffed',
                    border: domain.businessType === 'y' ? '1px solid #ffd591' : '1px solid #b7eb8f',
                  }}>
                    {domain.businessType === 'y' ? '测试空间' : '生产空间'}
                  </span>

                  <div style={{ marginBottom: '8px', paddingRight: '60px' }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {domain.domainName}
                    </h3>
                  </div>

                  <div style={{ marginBottom: '8px' }}>
                    <Tag color={
                      domain.permissionType === 'ADMIN' ? 'blue' :
                      domain.permissionType === 'MEMBER' ? 'green' : 'default'
                    }>
                      {domain.permissionType === 'ADMIN' ? '管理员' :
                       domain.permissionType === 'MEMBER' ? '成员' : '无权限'}
                    </Tag>
                  </div>

                  <div style={{ marginBottom: '6px', fontSize: '12px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <UserOutlined style={{ marginRight: '4px' }} />
                    <span>管理员：</span>
                    {(domain.adminUserInfoList || []).slice(0, 4).map((user, index) => (
                      <span key={user.workNo}>
                        {index > 0 && '、'}
                        {user.nickName || user.name}
                      </span>
                    ))}
                    {(domain.adminUserInfoList || []).length > 4 && (
                      <span>...</span>
                    )}
                  </div>

                  <div style={{ marginBottom: '6px', fontSize: '12px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <TeamOutlined style={{ marginRight: '4px' }} />
                    <span>成员：</span>
                    {(domain.memberUserInfoList || []).length > 0 ? (
                      <>
                        {domain.memberUserInfoList.slice(0, 4).map((user, index) => (
                          <span key={user.workNo}>
                            {index > 0 && '、'}
                            {user.nickName || user.name}
                          </span>
                        ))}
                        {domain.memberUserInfoList.length > 4 && (
                          <span>...</span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: '#999' }}>暂无</span>
                    )}
                  </div>

                  <div style={{ fontSize: '12px', color: '#999' }}>
                    创建时间：{formatDateTime(domain.gmtCreate)}
                  </div>
                </Card>
              </Col>
            ))
          )}
        </Row>
      </div>

      {/* 分页 */}
      <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`}
          onChange={handlePaginationChange}
          pageSizeOptions={['12', '24', '36', '48']}
        />
      </div>

      <Modal
        title={editingRecord ? '编辑项目空间' : '新增项目空间'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-4"
        >
          <Form.Item
            name="domainName"
            label="项目空间名称"
            rules={[
              { required: true, message: '请输入项目空间名称' },
              { max: 50, message: '项目空间名称不能超过50个字符' },
            ]}
          >
            <Input placeholder="请输入项目空间名称" />
          </Form.Item>

          <Form.Item
            name="adminUsers"
            label="管理员"
            rules={[
              { required: true, message: '请至少选择一名管理员' },
            ]}
          >
            <EmployeeSelector
              name="adminUsers"
              required={true}
              multiple={true}
              value={adminUsers}
              onChange={(value) => {
                setAdminUsers(value || []);
                adminUsersRef.current = value || [];
                // 同步更新表单字段的值
                form.setFieldsValue({ adminUsers: value || [] });
              }}
            />
          </Form.Item>

          <Form.Item
            name="businessType"
            label="空间类型"
            hidden={!editingRecord}
          >
            <Select
              options={[
                { label: '生产空间', value: 'n' },
                { label: '测试空间', value: 'y' },
              ]}
              placeholder="请选择空间类型"
            />
          </Form.Item>

          <div style={{ marginBottom: '15px' }}></div>

          <Form.Item
              name="memberUsers"
              label="成员"
              rules={[
                { required: false, message: '请至少选择一名成员' },
              ]}
            >
              <EmployeeSelector
                name="memberUsers"
                required={false}
                multiple={true}
                value={memberUsers}
                onChange={(value) => {
                  setMemberUsers(value || []);
                  memberUsersRef.current = value || [];
                  // 同步更新表单字段的值
                  form.setFieldsValue({ memberUsers: value || [] });
                }}
              />
          </Form.Item>


        </Form>
      </Modal>
    </div>
  );
}

export default BusinessDomainList;